import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useGifStore } from './gif-store';
import { GifPlaybackMode, GifQualityPreset, GIF_LIMITS, fpsToMs, QUALITY_PRESETS } from './types';
import { Repeat, ArrowRightLeft, Play, Settings2, FlipHorizontal2, Gauge } from 'lucide-react';

const playbackModes: { value: GifPlaybackMode; label: string; icon: React.ReactNode; description: string }[] = [
  { 
    value: 'one-shot', 
    label: 'One Shot', 
    icon: <Play className="h-4 w-4" />,
    description: 'Play once and stop'
  },
  { 
    value: 'loop', 
    label: 'Loop', 
    icon: <Repeat className="h-4 w-4" />,
    description: 'Repeat continuously'
  },
  { 
    value: 'bounce', 
    label: 'Bounce', 
    icon: <ArrowRightLeft className="h-4 w-4" />,
    description: 'Forward then backward'
  },
];

const qualityPresets: { value: GifQualityPreset; label: string; description: string }[] = [
  { value: 'low', label: 'Low', description: `${QUALITY_PRESETS.low.maxWidth}px, ${QUALITY_PRESETS.low.colors} colors` },
  { value: 'medium', label: 'Medium', description: `${QUALITY_PRESETS.medium.maxWidth}px, ${QUALITY_PRESETS.medium.colors} colors` },
  { value: 'high', label: 'High', description: `${QUALITY_PRESETS.high.maxWidth}px, ${QUALITY_PRESETS.high.colors} colors` },
];

interface GifSettingsProps {
  compact?: boolean;
}

export function GifSettings({ compact = false }: GifSettingsProps) {
  const settings = useGifStore((state) => state.settings);
  const updateSettings = useGifStore((state) => state.updateSettings);
  const setSpeedMultiplier = useGifStore((state) => state.setSpeedMultiplier);
  const reverseFrames = useGifStore((state) => state.reverseFrames);
  const frames = useGifStore((state) => state.frames);
  const speedMultiplier = settings.speedMultiplier ?? 1;

  const handlePlaybackModeChange = (value: string) => {
    updateSettings({ 
      playbackMode: value as GifPlaybackMode,
      loop: value === 'one-shot' ? 1 : 0,
    });
  };

  const handleQualityChange = (value: string) => {
    const quality = value as GifQualityPreset;
    const preset = QUALITY_PRESETS[quality];
    updateSettings({ 
      quality,
      maxWidth: preset.maxWidth,
    });
  };

  const handleFrameRateChange = (value: number[]) => {
    const fps = value[0];
    updateSettings({ 
      frameRate: fps,
      defaultFrameDuration: fpsToMs(fps),
    });
  };

  if (compact) {
    return (
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Mode:</Label>
          <Select value={settings.playbackMode} onValueChange={handlePlaybackModeChange}>
            <SelectTrigger className="w-32" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {playbackModes.map((mode) => (
                <SelectItem key={mode.value} value={mode.value}>
                  <span className="flex items-center gap-2">
                    {mode.icon}
                    {mode.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">FPS:</Label>
          <Input
            type="number"
            min={GIF_LIMITS.MIN_FPS}
            max={GIF_LIMITS.MAX_FPS}
            value={settings.frameRate}
            onChange={(e) => handleFrameRateChange([parseInt(e.target.value) || GIF_LIMITS.MIN_FPS])}
            className="w-16 h-8"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Quality:</Label>
          <Select value={settings.quality} onValueChange={handleQualityChange}>
            <SelectTrigger className="w-28" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {qualityPresets.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">GIF Settings</CardTitle>
        </div>
        <CardDescription>Configure your animated GIF output</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Playback Mode */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Playback Mode</Label>
          <div className="grid grid-cols-3 gap-2">
            {playbackModes.map((mode) => (
              <button
                key={mode.value}
                type="button"
                onClick={() => handlePlaybackModeChange(mode.value)}
                className={`
                  flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all
                  ${settings.playbackMode === mode.value 
                    ? 'border-primary bg-primary/5' 
                    : 'border-muted hover:border-muted-foreground/30'
                  }
                `}
              >
                <span className={settings.playbackMode === mode.value ? 'text-primary' : 'text-muted-foreground'}>
                  {mode.icon}
                </span>
                <span className="text-sm font-medium">{mode.label}</span>
                <span className="text-xs text-muted-foreground text-center">{mode.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Frame Rate */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Frame Rate</Label>
            <span className="text-sm text-muted-foreground">
              {settings.frameRate} FPS ({fpsToMs(settings.frameRate)}ms/frame)
            </span>
          </div>
          <Slider
            value={[settings.frameRate]}
            onValueChange={handleFrameRateChange}
            min={GIF_LIMITS.MIN_FPS}
            max={GIF_LIMITS.MAX_FPS}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Slower ({GIF_LIMITS.MIN_FPS} FPS)</span>
            <span>Faster ({GIF_LIMITS.MAX_FPS} FPS)</span>
          </div>
        </div>

        {/* Quality */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Quality Preset</Label>
          <div className="grid grid-cols-3 gap-2">
            {qualityPresets.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => handleQualityChange(preset.value)}
                className={`
                  flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all
                  ${settings.quality === preset.value 
                    ? 'border-primary bg-primary/5' 
                    : 'border-muted hover:border-muted-foreground/30'
                  }
                `}
              >
                <span className="text-sm font-medium">{preset.label}</span>
                <span className="text-xs text-muted-foreground">{preset.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Loop Count (only for loop mode) */}
        {settings.playbackMode === 'loop' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Loop Count</Label>
              <span className="text-sm text-muted-foreground">
                {settings.loop === 0 ? 'Infinite' : `${settings.loop} time${settings.loop > 1 ? 's' : ''}`}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Slider
                value={[settings.loop]}
                onValueChange={([value]) => updateSettings({ loop: value })}
                min={0}
                max={10}
                step={1}
                className="flex-1"
              />
              <Input
                type="number"
                min={0}
                max={100}
                value={settings.loop}
                onChange={(e) => updateSettings({ loop: parseInt(e.target.value) || 0 })}
                className="w-16"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              0 = infinite loop. Higher values limit the number of times the animation plays.
            </p>
          </div>
        )}

        {/* F8: Speed Multiplier */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Gauge className="h-4 w-4 text-muted-foreground" />
              Speed
            </Label>
            <span className="text-sm text-muted-foreground">{speedMultiplier}x</span>
          </div>
          <div className="grid grid-cols-6 gap-1">
            {[0.25, 0.5, 1, 1.5, 2, 3].map((mult) => (
              <button
                key={mult}
                type="button"
                onClick={() => setSpeedMultiplier(mult)}
                className={`rounded-md border-2 py-1.5 text-xs font-medium transition-all ${
                  speedMultiplier === mult
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-muted hover:border-muted-foreground/30'
                }`}
              >
                {mult}x
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Effective rate: {(settings.frameRate * speedMultiplier).toFixed(1)} FPS
          </p>
        </div>

        {/* F8: Reverse */}
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={reverseFrames}
            disabled={frames.length < 2}
            className="w-full gap-2"
          >
            <FlipHorizontal2 className="h-4 w-4" />
            Reverse Frame Order
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default GifSettings;
